<template>
  <div class="text-center" v-if="currentAdicional">
    <v-dialog
      v-model="currentAdicional"
      :max-width="$vuetify.breakpoint.mobile ? '100%' : '800'"
      :fullscreen="$vuetify.breakpoint.mobile"
      scrollable
      persistent
      :retain-focus="false"
    >
      <v-card v-if="currentAdicional.loading">
        <v-card-text class="pa-0">
          <div class="py-4 px-4 px-sm-8">
            <div class="d-flex flex-row align-center justify-space-between">
              <div class="d-flex flex-row align-center">
                <v-icon color="white" small class="info-icon mr-2">mdi-file-document-multiple-outline</v-icon>
                <p class="mb-0 font-weight-bold text-primary title">Cargando Adicional</p>
              </div>
              <v-btn class="mx-2" fab dark small color="#F4516C" @click="closeModal">
                <v-icon dark>mdi-close</v-icon>
              </v-btn>
            </div>
            <v-row align="center" class="mt-2">
              <v-col cols="12" md="12">
                <div class="d-flex flex-column text-center align-center py-1" style="width: 100%">
                  <v-progress-circular
                    :size="50"
                    :width="5"
                    color="#2D4B95"
                    indeterminate
                  ></v-progress-circular>
                  <p class="mb-0 body-2 ml-2">Cargando información</p>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
      </v-card>

      <v-card v-else>
        <!-- ─── HEADER ─── -->
        <v-card-title class="px-4 px-sm-8 py-3">
          <div
            class="d-flex align-center justify-space-between"
            style="width: 100%;"
            :class="$vuetify.breakpoint.mobile ? 'flex-column' : 'flex-row'"
          >
            <div class="d-flex flex-row align-center order-1 order-sm-0" :class="$vuetify.breakpoint.mobile ? 'mb-2 align-self-start' : ''">
              <v-icon color="white" small class="info-icon mr-2 flex-shrink-0">mdi-file-document-multiple-outline</v-icon>
              <p
                class="mb-0 font-weight-bold text-primary"
                :class="$vuetify.breakpoint.mobile ? 'body-2' : 'title'"
                style="line-height: 1.3; word-break: break-word;"
              >
                {{ currentAdicional.title }} - Nº {{ currentAdicional.number }}
              </p>
            </div>
            <div :class="$vuetify.breakpoint.mobile ? 'align-self-end' : ''">
              <HeaderButtons :closeModal="closeModal" :draftId="draftId"/>
            </div>
          </div>
          <div
            class="d-flex align-center justify-end"
            style="width: 100%;gap:8px;"
            :class="$vuetify.breakpoint.mobile ? 'flex-column' : 'flex-row'"
          >
						<p class="mb-0 text-primary" style="font-size: 10px;">Visto por:</p>
						<SeenByUsers
							v-model="seenByMenu"
							:seenByUsers="currentAdicional.seen_by || []"
						/>
						<v-divider vertical />
						<BotSummarizeMini v-if="!currentAdicional.loading" />
					</div>
        </v-card-title>

        <!-- ─── BODY ─── -->
        <v-card-text class="pa-0" ref="additionalCardContainer">
          <div class="py-4 px-4 px-sm-8">

            <!-- Info general -->
            <v-row align="start" dense>
              <!-- Columna izquierda -->
              <v-col cols="12" sm="6">
                <p class="mb-1 info-line" v-if="currentAdicional.ito_user">
                  <v-icon color="#2D4B95" small class="mr-2">mdi-account-outline</v-icon>
                  <span><b>ITO:</b> {{ currentAdicional.ito_user.name }}</span>
                </p>
                <p class="mb-1 info-line" v-if="currentAdicional.created_by_user">
                  <v-icon color="#2D4B95" small class="mr-2">mdi-account-outline</v-icon>
                  <span><b>Creador:</b> {{ currentAdicional.created_by_user.name }}</span>
                </p>
                <p class="mb-1 info-line">
                  <v-icon color="#2D4B95" small class="mr-2">mdi-code-brackets</v-icon>
                  <span><b>Código:</b> {{ currentAdicional.code }}</span>
                </p>
                <p class="mb-1 info-line">
                  <v-icon color="#2D4B95" small class="mr-2">mdi-clock-alert-outline</v-icon>
                  <span><b>Impacto:</b> {{ affectsTimelineText }}</span>
                </p>
                <p class="mb-1 info-line">
                  <v-icon color="#2D4B95" small class="mr-2">mdi-calendar-month-outline</v-icon>
                  <span><b>Fecha Emisión:</b> {{ currentAdicional.created_at | toDateTime }}</span>
                </p>
                <p class="mb-1 info-line" v-if="currentAdicional.critical_date">
                  <v-icon color="#2D4B95" small class="mr-2">mdi-calendar-month-outline</v-icon>
                  <span><b>Fecha Crítica:</b> {{ currentAdicional.critical_date | toDate }}</span>
                </p>
                <p class="mb-1 info-line">
                  <v-icon color="#2D4B95" small class="mr-2">mdi-clock-time-eight-outline</v-icon>
                  <span><b>Tiempo de espera:</b> {{ currentAdicional.created_at | waitingTime }}</span>
                </p>
              </v-col>

              <!-- Columna derecha -->
              <v-col cols="12" sm="6">
                <!-- Estado -->
                <div class="mb-2 d-flex align-center flex-wrap" style="gap: 6px;">
                  <b class="text-primary">Estado:</b>
                  <v-chip :color="statusColor" x-small outlined>
                    {{ currentAdicional.statustext }}
                  </v-chip>
                  <v-tooltip bottom v-if="checkIfNearDeadline(currentAdicional)">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon color="white" class="icon-top medium" v-bind="attrs" v-on="on" small>
                        mdi-flag-outline
                      </v-icon>
                    </template>
                    <span>Se acerca la fecha crítica {{ currentAdicional.critical_date | toDate }}</span>
                  </v-tooltip>
                </div>

                <p class="mb-1 info-line" v-if="currentAdicional.ended_at">
                  <b class="text-primary">{{ endedDateText }}:</b>
                  <span class="ml-1">{{ currentAdicional.ended_at | toDateTime }}</span>
                </p>
                <p class="mb-1 info-line">
                  <b class="text-primary">Proyecto:</b>
                  <span class="ml-1">{{ currentAdicional.project.name }}</span>
                </p>
                <p class="mb-1 info-line">
                  <b class="text-primary">Tipo:</b>
                  <span class="ml-1">{{ additionalType }} - </span>
                  <span class="light-blue-text font-weight-bold ml-1">{{ additionalCostType }}</span>
                </p>

                <!-- Especialidades -->
                <div class="d-flex align-center flex-wrap mb-2" style="gap: 4px;">
                  <b class="text-primary mr-1">Especialidades:</b>
                  <v-chip
                    v-for="(specialty, index) in currentAdicional.specialties"
                    :key="index"
                    color="#5E85EF"
                    dark
                    small
                  >
                    <small>{{ specialty.specialty.name }}</small>
                  </v-chip>
                </div>
              </v-col>
            </v-row>
						<div class="d-flex align-center justify-space-between">
							<div class="d-flex">
                <ItemsAssociationInput :item="currentAdicional" item_type="additional" :beforeNavigate="minimizeItem"/>
							</div>

							<!-- Asociaciones + Crear Elementos -->
							<div>
								<v-menu offset-y>
									<template v-slot:activator="{ on, attrs }">
										<v-btn rounded color="#2D4B95" dark x-small v-bind="attrs" v-on="on">
											Crear Elementos
										</v-btn>
									</template>
									<div class="d-flex flex-column pa-2 white" style="min-width: 220px;">
										<v-btn
											v-if="checkPermissions(['create_tasks'])"
											class="my-1"
											rounded color="#5E85EF" dark x-small block
											@click="sendToTask()"
										>
											<v-icon dark small>mdi-clipboard-text-multiple-outline</v-icon>
											<p class="mb-0 ml-2">Enviar a Tarea</p>
										</v-btn>
										<v-btn
											v-if="checkPermissions(['create_tasks'])"
											class="my-1"
											rounded color="#5E85EF" dark x-small block
											@click="sendToAdditionalLikely()"
										>
											<v-icon dark small>mdi-clipboard-text-multiple-outline</v-icon>
											<p class="mb-0 ml-2">Enviar a Posible Adicional</p>
										</v-btn>
										<v-btn
											v-if="checkPermissions(['create_rdi'])"
											class="my-1"
											rounded color="#5E85EF" dark x-small block
											@click="sendToRDI()"
										>
											<v-icon dark small>mdi-clipboard-text-multiple-outline</v-icon>
											<p class="mb-0 ml-2">Enviar a RDI</p>
										</v-btn>
										<v-btn
											v-if="checkPermissions(['create_additionals'])"
											class="my-1"
											rounded color="#5E85EF" dark x-small block
											@click="sendToAdditional()"
										>
											<v-icon dark small>mdi-clipboard-text-multiple-outline</v-icon>
											<p class="mb-0 ml-2">Enviar a Adicional</p>
										</v-btn>
									</div>
								</v-menu>
							</div>
						</div>

            <v-divider class="my-3"></v-divider>

            <!-- Descripción General -->
						<div>
							<h4 class="mb-2 text-primary">Descripción General</h4>
							<div v-html="firstRevision.description" class="description-content"></div>
						</div>

            <v-divider class="my-3"></v-divider>

						<!-- Observaciones Generales -->
						<div>
							<AdditionalGeneralObservations
								:additional="currentAdicional"
								:needToReloadListing="() => need_reload = true"
								@minimizeParent="minimizeItem"
                :beforeNavigate="minimizeItem"
							/>
						</div>

            <v-divider class="my-3"></v-divider>

            <!-- Archivos adjuntos -->
            <v-row align="center" v-if="files.length > 0" dense>
              <v-col cols="12">
                <div class="d-flex flex-wrap" style="gap: 8px;">
                  <div v-for="(file, index) in files" :key="index" class="attachment-item">
                    <div v-if="filesPreview[index] && filesPreview[index].src" class="preview-img-container">
                      <v-btn icon color="#5E85EF" x-small class="button-preview-img" @click="removeAttachment(index)">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                      <img :src="filesPreview[index].src" class="preview-img"/>
                    </div>
                    <div v-else class="d-flex flex-row align-center justify-start">
                      <v-btn icon color="#5E85EF" x-small @click="removeAttachment(index)">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                      <span class="caption">{{ file.name }}</span>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
            <v-divider class="my-3" v-if="files.length > 0"></v-divider>

            <!-- Revisiones -->
            <v-row align="center" dense>
              <v-col cols="12">
                <RevisionSingleContainer
                  v-for="(revision, index) in showingRevisions"
                  :key="index"
                  :revision="revision"
                  :currentAdicional="currentAdicional"
                  :isFirstRevision="firstRevision.id == revision.id"
                  :isLastRevision="lastRevision.id == revision.id"
                  :needToReloadListing="() => need_reload = true"
									:beforeNavigate="minimizeItem"
                  @scrollToAddObservation="scrollToAddObservation"
                  @draftIdSet="setDraftId"
									@minimizeParent="minimizeItem"
                />
                <v-btn
                  v-if="currentAdicional.revisions.length > 1"
                  class="mt-1"
                  rounded color="#5E85EF" dark x-small text block
                  :loading="loadingFilePath"
                  @click="showingAllRevisions = !showingAllRevisions"
                >
                  <v-icon dark small>{{ showingAllRevisions ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                  <p class="mb-0 ml-2">{{ showingAllRevisions ? 'Mostrar sólo la última revisión' : 'Ver todas las revisiones' }}</p>
                </v-btn>
              </v-col>
            </v-row>

            <!-- Banner de estado -->
            <v-row align="center" dense class="mt-2">
              <v-col cols="12">
                <div class="status-container px-2 py-2 rounded-lg" :class="statusColor">
                  <p class="mb-0 white--text w-100 text-center body-2 font-weight-bold">
                    {{ statusText }} {{currentAdicional.status}}
                  </p>
                </div>
              </v-col>
            </v-row>

            <!-- Nueva Revisión -->
            <v-scroll-y-transition>
              <v-row align="center" dense v-if="showAddRevision">
                <v-col cols="12">
                  <div class="px-0 py-1" style="width: 100%">
                    <v-divider class="my-2"></v-divider>
                    <AddRevision ref="addRevisionContainer" :closeAddRevision="closeAddRevision" :additional="currentAdicional"/>
                    <v-divider class="my-2"></v-divider>
                  </div>
                </v-col>
              </v-row>
            </v-scroll-y-transition>

            <!-- Barra de acciones -->
						<v-row align="center" v-if="canPerformActions" class="mt-2">
							<v-col cols="12">
								<v-divider class="mb-3"></v-divider>

								<!-- Mensaje: sin acciones posibles (contraparte esperando) -->
								<div
									v-if="showNoActionsMessage && false"
									class="pa-3 rounded grey lighten-4"
								>
									<p class="mb-0 font-weight-bold caption">No hay acciones posibles.</p>
									<p class="mb-0 caption text--secondary">
										Este adicional se encuentra en espera de acción de la contraparte.
									</p>
								</div>

								<!-- Acciones disponibles -->
								<div
									v-else-if="![3,5].includes(currentAdicional.status)"
									class="d-flex flex-wrap justify-end"
									style="gap: 8px;"
								>
									<template v-if="![3,5,6].includes(currentAdicional.status)">
										<!-- Rechazar solicitud de anulación (contraparte que NO solicitó la anulación) -->
										<v-btn
											v-if="showRejectCancelRequestBtn"
											rounded
											color="error"
											dark
											small
											:loading="loadingChangeStatus"
											@click="changeStatusAdditional(
												currentAdicional.userprojectjobtype.job_type_id == 4
													? additionalStatuses.REV_COMPANY
													: additionalStatuses.REV_ITO
											)"
										>
											<v-icon small class="mr-1">mdi-close-circle-outline</v-icon>
											Rechazar Solicitud de Anulación
										</v-btn>

										<!-- Cancelar solicitud de anulación (quien la solicitó) -->
										<v-btn
											v-if="showCancelOwnRequestBtn"
											rounded
											color="warning"
											dark
											small
											:loading="loadingChangeStatus"
											@click="changeStatusAdditional(
												currentAdicional.userprojectjobtype.job_type_id == 4
													? additionalStatuses.REV_COMPANY
													: additionalStatuses.REV_ITO
											)"
										>
											<v-icon small class="mr-1">mdi-undo</v-icon>
											Cancelar Solicitud de Anulación
										</v-btn>

										<!-- Solicitar Anulación (ambas partes, solo si no está ya solicitada) -->
										<v-btn
											v-if="showRequestCancelBtn"
											rounded
											color="#5E85EF"
											dark
											small
											:loading="loadingChangeStatus"
											@click="changeStatusAdditional(additionalStatuses.CANCELLED)"
										>
											<v-icon small class="mr-1">mdi-cancel</v-icon>
											Solicitar Anulación
										</v-btn>

										<!-- Anular Adicional (solo parte responsable cuando ya está solicitada) -->
										<v-btn
											v-if="showConfirmCancelBtn"
											rounded
											color="error"
											dark
											small
											:loading="loadingChangeStatus"
											@click="changeStatusAdditional(additionalStatuses.CANCELLED)"
										>
											<v-icon small class="mr-1">mdi-cancel</v-icon>
											Anular Adicional
										</v-btn>

										<!-- Sin Acuerdo -->
										<v-btn
											v-if="showNoAgreementAndRevisionBtns"
											rounded
											color="#5E85EF"
											dark
											small
											:loading="loadingChangeStatus"
											@click="changeStatusAdditional(additionalStatuses.NO_AGREEMENT)"
										>
											<v-icon small class="mr-1">mdi-handshake-outline</v-icon>
											Sin Acuerdo
										</v-btn>
									</template>

									<!-- Nueva Revisión -->
									<v-btn
										v-if="showNoAgreementAndRevisionBtns"
										rounded
										color="#5E85EF"
										dark
										small
										@click="toggleAddRevisionVisibility()"
									>
										<v-icon small class="mr-1">mdi-file-document-edit-outline</v-icon>
										Nueva Revisión
									</v-btn>

								</div>

								<v-divider class="my-3"></v-divider>

								<div class="d-flex justify-end">
									<AdditionalClosure
										v-if="![3,5,6].includes(currentAdicional.status)"
										:additional="currentAdicional"
										@changeStatusAdditional="changeStatusAdditional"
										@reloadInfo="startGetAdditionalByID(true, true, true)"
									/>
								</div>
							</v-col>
						</v-row>

						<ClosureSignature :item="currentAdicional" v-if="currentAdicional.status == additionalStatuses.ACCEPTED"/>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <AcceptCancelModal
      :acceptRejectModal="acceptRejectModal"
      :acceptRejectModalData="acceptRejectModalData"
      :closeModal="closeModalAcceptCancel"
    />
    <SendContentByEmailModal
      :sendContentByEmailModal="sendByEmailModal"
      :closeModal="() => sendByEmailModal = false"
      :sendingType="2"
    />
  </div>
</template>

<script>
import moment from 'moment'
import EventBus from '@/helpers/event_bus'
import _debounce from 'lodash/debounce'
import {getPublicUrl, getTareaByID} from '@/helpers/api/tasks'
import { getZippedFiles } from '@/helpers/api/fileUploads'
import {getRdiByID} from '@/helpers/api/rdi'
import {changeAdditionalStatus, addAdditionalAssociations, removeAdditionalAssociations} from '@/helpers/api/additionals'
import {search} from '@/helpers/api/general'
import SendContentByEmailModal from '@/components/Globals/Modals/General/SendContentByEmailModal'
import AcceptCancelModal from '@/components/Globals/Modals/General/AcceptCancelModal.vue'
import HeaderButtons from '@/components/Globals/Modals/Adicionales/Parts/HeaderButtons'
import RevisionSingleContainer from '@/components/Globals/Modals/Adicionales/Parts/RevisionSingleContainer'
import AddRevision from '@/components/Globals/Modals/Adicionales/AddRevision'
import ItemsAssociationInput from '@/components/Globals/Helpers/ItemsAssociationInput'
import AdditionalClosure from '@/components/Views/Adicionales/Parts/AdditionalClosure'
import ClosureSignature from '@/components/Globals/Helpers/ClosureSignature'
import additionalStatuses from '@/helpers/constants/additional.js'
import AdditionalGeneralObservations from '@/components/Globals/Modals/Adicionales/Parts/AdditionalGeneralObservations'
import BotSummarizeMini from '@/components/Globals/Helpers/BotSummarizeMini'
import SeenByUsers from '@/components/Globals/Helpers/SeenByUsers'

export default {
  name: 'AdicionalModal',
  components: { SendContentByEmailModal, AcceptCancelModal, HeaderButtons, RevisionSingleContainer, AddRevision, ItemsAssociationInput, AdditionalClosure, ClosureSignature, AdditionalGeneralObservations, BotSummarizeMini, SeenByUsers },
  computed: {
    affectsTimelineText(){
      switch (this.currentAdicional.impacts_timeline) {
        case 1: return 'Afecta el Plazo'
        case 2: return 'No Afecta el Plazo'
        case 3: return 'Se evaluará'
        default: return 'Otro'
      }
    },
    firstRevision(){
      return this.currentAdicional.revisions[0]
    },
    lastRevision(){
      const singleIndexShow = this.currentAdicional.revisions.length - 1
      return this.currentAdicional.revisions[singleIndexShow]
    },
    additionalType(){
      switch (this.currentAdicional.type) {
        case 1: return 'Obra Extraordinaria'
        case 2: return 'Aumento de Obra'
        case 3: return 'Disminución de Obra'
        default: return 'Otro'
      }
    },
    additionalCostType(){
      switch (this.currentAdicional.cost_type) {
        case 1: return 'Suma Alzada [SA]'
        case 2: return 'Serie de Precios Unitarios [SPU]'
        case 3: return 'Valor Proforma [VPF]'
        default: return 'Otro'
      }
    },
    endedDateText(){
      if(this.currentAdicional.status == additionalStatuses.ACCEPTED) return 'Fecha Aprobación'
      else if(this.currentAdicional.status == additionalStatuses.CANCELLED) return 'Fecha Anulación'
      return 'Fecha Cierre'
    },
    canPerformActions(){
      return this.currentAdicional.status != additionalStatuses.CANCELLED &&
             this.currentAdicional.status != additionalStatuses.ACCEPTED &&
             this.checkPermissions(['change_status_additionals'])
    },
    additionalAssociations(){
      const types = {
        'App\\Models\\Additional': 'additional',
        'App\\Models\\Rdi': 'rdi',
        'App\\Models\\TasksRestrictions': 'task'
      }
      return this.currentAdicional.item_associations.map((association) => {
        if(association.first_morphable_type == 'App\\Models\\Additional'){
          return {...association.second_morphable, ...{association_id: association.id, type: types[association.second_morphable_type]}}
        }
        return {...association.first_morphable, ...{association_id: association.id, type: types[association.first_morphable_type]}}
      })
    },
    currentAdicional() {
      return this.$store.adicionalesStore.state.currentAdicional
    },
    statusColor(){
      switch (this.currentAdicional.status) {
        case 1: return 'primary'
        case 2: return 'primary'
        case 3: return 'success'
        case 4: return 'warning'
        case 5: return 'error'
        case 6: return 'error'
        case 7: return 'warning'
        default: return 'primary'
      }
    },
    statusText(){
      switch (this.currentAdicional.status) {
        case 1: return 'Adicional creado, a la espera de ser respondido por ITO: ' + this.responsableUserName
        case 2: return 'Adicional creado, a la espera de ser respondido por Empresa: ' + this.responsableUserName
        case 3: return 'Se ha aprobado el adicional'
        case 4:
          if(this.currentAdicional.response_status == 2) return 'ITO ha solicitado anular el adicional'
          return 'Constructora ha solicitado anular el adicional'
        case 5: return 'El adicional ha sido anulado'
        case 6: return 'Sin Acuerdo'
        case 7: return 'Observado por ITO, a la espera de acción por parte de la Empresa'
        default: return 'primary'
      }
    },
    iAmResponsablePart(){
      const resp_no_ito = this.currentAdicional.response_status === 2 && this.currentAdicional.userprojectjobtype.job_type_id !== 4
      const resp_ito = this.currentAdicional.response_status === 1 && this.currentAdicional.userprojectjobtype.job_type_id === 4
      return resp_no_ito || resp_ito
    },
    responsableUserName(){
      if(this.iAmResponsablePart && this.currentAdicional.response_status === 2){
        return this.currentAdicional.created_by_user.name
      }
      return this.currentAdicional.ito_user ? 'ITO: ' + this.currentAdicional.ito_user.name : 'ITO'
    },
    showingRevisions(){
      if(this.showingAllRevisions){
        return this.currentAdicional.revisions
      }
      const singleIndexShow = this.currentAdicional.revisions.length - 1
      return [ this.currentAdicional.revisions[singleIndexShow] ]
    },
		showNoActionsMessage() {
			// Contraparte que NO solicitó nada y el adicional no está en cancel_request
			return !this.iAmResponsablePart &&
				this.currentAdicional.userprojectjobtype.job_type_id == 4 &&
				this.currentAdicional.status != this.additionalStatuses.CANCEL_REQUEST
		},
		showRejectCancelRequestBtn() {
			// La contraparte que RECIBE la solicitud (debe actuar = iAmResponsablePart true)
			return this.iAmResponsablePart &&
				this.currentAdicional.status == this.additionalStatuses.CANCEL_REQUEST &&
				this.checkPermissions(['change_status_additionals'])
		},
		showCancelOwnRequestBtn() {
			// Quien INICIÓ la solicitud quiere revertirla (ya actuó = iAmResponsablePart false)
			return !this.iAmResponsablePart &&
				this.currentAdicional.status == this.additionalStatuses.CANCEL_REQUEST &&
				this.checkPermissions(['change_status_additionals'])
		},
		showCancelBtn() {
			// Solo la parte responsable puede solicitar/confirmar anulación
			return this.iAmResponsablePart && this.checkPermissions(['change_status_additionals'])
		},
		showNoAgreementAndRevisionBtns() {
			return this.currentAdicional.userprojectjobtype.job_type_id !== 4 &&
				this.checkPermissions(['change_status_additionals']) &&
				this.canAddRevisions()
		},
		showRequestCancelBtn() {
			// Ambas partes pueden solicitar anulación, siempre que no esté ya solicitada
			return this.currentAdicional.status !== this.additionalStatuses.CANCEL_REQUEST &&
				this.checkPermissions(['change_status_additionals'])
		},
		showConfirmCancelBtn() {
			// Solo la parte responsable puede confirmar/anular cuando ya está solicitada
			return this.iAmResponsablePart &&
				this.currentAdicional.status == this.additionalStatuses.CANCEL_REQUEST &&
				this.checkPermissions(['change_status_additionals'])
		},
  },
  watch: {},
  data: () => ({
		showObservationEditor: false,
		newObservation: '',
    additionalStatuses,
    fab: false,
    dialog: true,
    associationsMenu: false,
    searchForAssociation: null,
    showMore: false,
    showAddRevision: false,
    showingAllRevisions: false,
    interactionSearch: '',
    message: '',
    response_type: 0,
    need_reload: false,
    files: [],
    filesPreview: [],
    loadingFilePath: false,
    is_response: false,
    loading: false,
    changeInChecklists: false,
    loadingChangeStatus: false,
    loadingChangeUser: false,
    loadingPDFDownload: false,
    sendByEmailModal: false,
    cancelTimer: 4,
    inCancelTime: false,
    cancelInterval: null,
    confirmButtonText: 'Responder',
    loadingEdit: false,
    acceptRejectModal: false,
    acceptRejectModalData: null,
    showResults: false,
    searchResultsCount: 0,
    searching: false,
    searchResult: {},
    requiredRule: [
      v => !!v || 'Campo requerido'
    ],
    draftId: null,
		seenByMenu: false
  }),
  mounted(){
    this.setListeners()
  },
  beforeDestroy(){
    EventBus.$off('completedFileUpload')
		EventBus.$off('reload_element')
  },
  methods: {
    scrollToAddObservation(){
      this.$nextTick(() => {
        const card = this.$refs.additionalCardContainer;
        if (card) {
          card.scrollTo({ top: card.scrollHeight, behavior: 'smooth' });
        }
      })
    },
    setDraftId(draft_id){
      console.log('Draft ID set to:', draft_id)
      this.draftId = draft_id
    },
    sendToTask(){
			const t = this.currentAdicional
			const associations = [{ type: 'additional', id: t.id, title: t.title, number: t.number }]
      const data = {
        title: t.title,
        description: t.description,
        associations
      }
      this.$store.consultasStore.commit('setBorradorTarea', data)
      this.$store.consultasStore.commit('setCreateTareaModal', true)
    },
    sendToAdditionalLikely(){
			const t = this.currentAdicional
			const associations = [{ type: 'additional', id: t.id, title: t.title, number: t.number }]
      const data = {
        title: t.title,
        description: t.description,
        associations
      }
      this.$store.consultasStore.commit('setBorradorTarea', data)
      this.$store.adicionalesStore.commit('setCreatePosibleAdicionalModal', true)
    },
    sendToRDI(){
			const t = this.currentAdicional
			const associations = [{ type: 'additional', id: t.id, title: t.title, number: t.number }]
      const data = {
        title: t.title,
        description: t.description,
        associations
      }
      this.$store.rdiStore.commit('setInitialDataRDI', data)
      this.$store.rdiStore.commit('setCreateRDIModal', true)
    },
    sendToAdditional(){
			const t = this.currentAdicional
			const associations = [{ type: 'additional', id: t.id, title: t.title, number: t.number }]
      const data = {
        title: t.title,
        description: t.description,
        associations
      }
      this.$store.adicionalesStore.commit('setInitialDataAdicional', data)
      this.$store.adicionalesStore.commit('setCreateAdicionalModal', true)
      this.closeModal()
    },
    async removeAssociation(association){
      await removeAdditionalAssociations({association_id: association.association_id})
      this.$store.adicionalesStore.dispatch('getAdditionalByID', {
        additional_id: this.currentAdicional.id,
        seen_by_me: true,
        dontSetLoading: true
      })
    },
    sendToSearch: _debounce(function(){ this.searchForString(this.searchForAssociation) }, 800),
    async searchForString(searchString){
      this.searching = true
      const data = {
        search: searchString,
        project_id: this.$store.projectsStore.state.selectedProject.id,
        exclude_description: true
      }
      const searchResult = await search(data)
      if(searchResult.code == 200){
        this.searchResult = searchResult
        this.searchResultsCount = searchResult.tasks.length + searchResult.rdis.length
        this.showResults = true
      }
      this.searching = false
    },
    async closeResults(info = null){
      if(info){
        const data = {
          additional_id: this.currentAdicional.id,
          item_id: info.id,
          item_type: info.type
        }
        await addAdditionalAssociations(data)
        this.$store.adicionalesStore.dispatch('getAdditionalByID', {
          additional_id: this.currentAdicional.id,
          seen_by_me: true,
          dontSetLoading: true
        })
      }
      this.associationsMenu = false
      this.showResults = false
      this.searching = false
    },
    handleMoveTo(association){
      switch (association.type) {
        case 'RDI':
          this.viewRDI(association.id)
          break;
        case 'Tarea':
          this.viewTarea(association.id)
          break;
        default:
          break;
      }
    },
    async viewRDI(rdi_id){
      this.$store.rdiStore.commit('setCurrentRDI', {loading: true})
      const rdi = await getRdiByID({rdi_id})
      if(rdi.code === 200){
        this.$store.rdiStore.commit('setCurrentRDI', rdi.rdi)
      }
    },
    async viewTarea(task_id){
      this.$store.consultasStore.commit('setCurrentTarea', {loading: true})
      const tarea = await getTareaByID({task_id})
      if(tarea.code === 200){
        this.$store.consultasStore.commit('setCurrentTarea', tarea.task)
      }
    },
    canAcceptAditional(){
      return this.currentAdicional.userprojectjobtype.job_type_id == 4
    },
    canAddRevisions(){
      return this.currentAdicional.userprojectjobtype.job_type_id !== 4
    },
    checkInteractPermission(permissionToCheck){
      return this.checkPermissions(permissionToCheck)
    },
    changeInSubscription(value){
      this.editRdiForm.subscribe_status_mail = value
    },
    isUserPart(){
      return this.$store.userStore.state.userInfo.id === this.$store.rdiStore.state.currentRDI.created_by_user.id ||
             this.$store.userStore.state.userInfo.id === this.$store.rdiStore.state.currentRDI.responsable_user.id
    },
    isMyRdi(){
      return this.$store.userStore.state.userInfo.id === this.$store.rdiStore.state.currentRDI.created_by_user.id
    },
    filteredSearch(items){
      if(this.interactionSearch && this.interactionSearch !== '' && items){
        return items.filter((item) => item.message.toLowerCase().includes(this.interactionSearch.toLowerCase()))
      }
      return items
    },
    toggleAddRevisionVisibility(){
      this.showAddRevision = !this.showAddRevision
      setTimeout(() => {
        this.$vuetify.goTo(this.$refs.addRevisionContainer)
      }, 2000)
    },
    changedChecklist(){
      this.changeInChecklists = true
    },
    setListeners(){
      EventBus.$on('completedFileUpload', async (payload) => {
        if(payload && payload.additional_id && this.currentAdicional && payload.additional_id == this.currentAdicional.id){
          this.startGetAdditionalByID(true, true)
        }
      })
      EventBus.$on('reload_element', async (payload) => {
        if (payload?.type == 'additional' && payload?.id == this.currentAdicional.id){
          this.startGetAdditionalByID(true, true)
        }
      })
    },
    startGetAdditionalByID: _debounce(function(seen_by_me = false, dontSetLoading = false, getListing = false){ this.getAdditionalByID(seen_by_me, dontSetLoading, getListing) }, 800),
    getAdditionalByID(seen_by_me = false, dontSetLoading = false, getListing = false){
      this.$store.adicionalesStore.dispatch('getAdditionalByID', {
        additional_id: this.currentAdicional.id,
        seen_by_me,
        dontSetLoading
      })
			if(getListing){ this.getAdditionalList() }
    },
    closeAddRevision(){
      this.showAddRevision = false
      this.need_reload = true
    },
    async getAdditionalList(){
      let project_id = null
      if(this.$store.projectsStore.state.selectedProject){
        project_id = this.$store.projectsStore.state.selectedProject.id
      }
      else{
        const id = localStorage.getItem('selectedProjectID')
        project_id = parseInt(id)
      }
      if(project_id){
        this.$store.adicionalesStore.dispatch('getAdditionals', {project_id})
      }
    },
    checkIfNearDeadline(add){
      if(!add.critical_date){ return false }
      const now = moment()
      const add_date = moment(add.critical_date)
      return add_date.diff(now, 'days') <= 2
    },
    getColorStatus(){
      if(this.$store.rdiStore.state.currentRDI.status === 1) return '#3EBFA3'
      return '#F4516C'
    },
    closeModal(){
      const reload = this.need_reload || !this.currentAdicional.prev_seen_by_me
      this.$store.adicionalesStore.commit('setCurrentAdicional', null)
      if(reload){ this.getAdditionalList() }
      this.need_reload = false
      this.showAddRevision = false
      this.loading = false
      this.files = []
      this.filesPreview = []
    },
    onButtonClick() {
      this.$refs.uploader.click()
    },
    async onFileChanged(e) {
      const newFile = e.target.files[0]
      this.files.push(newFile)
      if(newFile.type.includes('image')){
        const base64File = await this.toBase64(newFile)
        this.filesPreview.push({src: base64File})
      }
      else{
        this.filesPreview.push({noImage: true})
      }
    },
    removeAttachment(index){
      this.files.splice(index, 1)
      this.filesPreview.splice(index, 1)
    },
    toBase64(file) {
      return new Promise((resolve) => {
        var reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = function () { resolve(reader.result) };
        reader.onerror = function () { resolve(null) };
      })
    },
    async getFilesUrl(attachments, singleFile = false, download = false){
      if(singleFile){ attachments = [attachments] }
      this.loadingFilePath = true
      const files = await getPublicUrl({attachments, force_download: download})
      if(files.code === 200){
        files.files.forEach((file) => { window.open(file.url) })
      }
      this.loadingFilePath = false
    },
    closeModalAcceptCancel(status = false){
      const modalData = this.acceptRejectModalData
      this.acceptRejectModal = false
      this.acceptRejectModalData = null
      if(!status || !modalData) return
      if(modalData.action === 'approveAdditional'){
        return this.executeChangeStatusAdditional(modalData.status)
      }
      if(modalData.type === 1){ this.closeRDI() }
      if(modalData.type === 2){ this.deleteRdi() }
    },
    async minimizeItem(draftId = this.draftId){
      const data = {
        task_id: this.currentAdicional.id,
        title: this.currentAdicional.title,
        type: 'Adicional',
        type_id: 5,
        number: this.currentAdicional.number,
        draft_id: draftId
      }
      await this.$nextTick()
      this.$store.globalStore.commit('addTaskToTasksHandler', data)
      this.closeModal()
    },
    async downloadPDF(){
      this.loadingPDFDownload = true
      const pdf_url = await getZippedFiles({rdi_id: this.currentAdicional.id})
      if(pdf_url.code == 200){ window.open(pdf_url.pdf_url) }
      this.loadingPDFDownload = false
    },
    async changeStatusAdditional(newStatus){
      if(newStatus === additionalStatuses.ACCEPTED){
        this.acceptRejectModalData = {
          action: 'approveAdditional',
          status: additionalStatuses.ACCEPTED,
          type: 'warning-pill',
          icon: 'mdi-check-decagram-outline',
          title: '¿Aprobar adicional Nº ' + this.currentAdicional.number + '?',
          content_a: '¿Está seguro de que desea aprobar este adicional?',
          content_b: 'La aprobación cerrará el adicional y registrará esta decisión.',
          cancelLabel: 'Cancelar',
          acceptLabel: 'Aprobar adicional'
        }
        this.acceptRejectModal = true
        return
      }
      return this.executeChangeStatusAdditional(newStatus)
    },
    async executeChangeStatusAdditional(newStatus){
      this.loadingChangeStatus = true
      let newStatusFinal = newStatus
      if( newStatus == additionalStatuses.CANCELLED && this.currentAdicional.status != additionalStatuses.CANCEL_REQUEST ){
        newStatusFinal = additionalStatuses.CANCEL_REQUEST
      }
      await changeAdditionalStatus({
        additional_id: this.currentAdicional.id,
        new_status: newStatusFinal
      })
      await this.$store.adicionalesStore.dispatch('getAdditionalByID', {
        additional_id: this.currentAdicional.id,
        seen_by_me: true,
        dontSetLoading: true
      })
      this.need_reload = true
      this.loadingChangeStatus = false
    }
  }
};
</script>

<style scoped>
  .info-container { color: #606060; }
  .text-primary { color: #2D4B95; }

  .info-icon {
    background-color: #2D4B95;
    border-radius: 25px;
    padding: 3px;
    width: 35px;
    height: 35px;
    flex-shrink: 0;
  }
  .info-icon.small-icon { width: 25px; height: 25px; }

  /* Info lines: icono + texto sin overflow */
  .info-line {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    font-size: 0.875rem;
    word-break: break-word;
  }
  .info-line .v-icon { flex-shrink: 0; margin-top: 1px; }

  .description-content {
    font-size: 0.875rem;
    line-height: 1.6;
    word-break: break-word;
  }

  /* Attachments */
  .attachment-item { flex: 0 0 auto; }
  .preview-img-container { position: relative; }
  .preview-img {
    width: 50px;
    height: 50px;
    border-radius: 10px;
    object-fit: contain;
  }
  .button-preview-img {
    position: absolute;
    top: -1px;
    left: -1px;
    background-color: white;
  }

  .light-blue-text { color: #5E85EF; }
  .bg-light-blue-text { background-color: #5E85EF; }

  .icon-top {
    padding: 2px;
    margin: 0 2px;
    border-radius: 100%;
  }
  .icon-top.high   { background-color: #F4516C; }
  .icon-top.medium { background-color: #fb8c00; }
  .icon-top.normal { background-color: #3EBFA3; }
  .icon-top.primary { background-color: #2D4B95; }
  .icon-top.light-blue { background-color: #5E85EF; }

  .status-container { width: 100%; }

  /deep/ .is_response_interaction .tiptap-vuetify-editor__content {
    border: 2px solid #3ebfa3;
  }
  .is_response_interaction_bottom_text { color: #3ebfa3; }

  /* En mobile el diálogo fullscreen no tiene bordes redondeados */
  @media (max-width: 600px) {
    /deep/ .v-dialog--fullscreen > .v-card {
      border-radius: 0 !important;
    }
  }
</style>